import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

// Import our API service to make requests
import api from '../services/api.service';

/**
 * User Role Manager Component
 * 
 * This component allows admin users to manage roles for users in the system.
 * It provides functionality to:
 * - View users and their current roles
 * - Add new role assignments
 * - Edit existing role assignments
 * - Remove role assignments
 */
const UserRoleManager = () => {
  // State for the user list
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // State for the add/edit user form
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [selectedRoles, setSelectedRoles] = useState({
    admin: false,
    manager: false,
    technician: false,
    user: true,
    viewer: false
  });
  const [editingUserId, setEditingUserId] = useState(null);
  
  // Dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    userId: null,
    email: ''
  });

  // Snackbar state for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Available roles - fetched from API
  const [availableRoles, setAvailableRoles] = useState([]);
  
  // Fetch users and roles on component mount
  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);
  
  /**
   * Fetch available roles from the API
   */
  const fetchRoles = async () => {
    try {
      const response = await api.get('/auth/roles');

      // The API service automatically unwraps { success: true, data: {...} }
      // So response.data is already the object with roles and descriptions
      const data = response.data.data ? response.data.data : response.data;
      const { roles, descriptions } = data;

      // Transform roles into the format expected by the UI
      const rolesArray = roles.map(roleId => ({
        id: roleId,
        label: roleId.charAt(0).toUpperCase() + roleId.slice(1),
        description: descriptions[roleId] || ''
      }));

      setAvailableRoles(rolesArray);

      // Initialize selectedRoles state with all roles set to false
      const initialRoles = {};
      roles.forEach(role => {
        initialRoles[role] = role === 'user'; // Default 'user' to true
      });
      setSelectedRoles(initialRoles);
    } catch (err) {
      console.error('Error fetching roles:', err);
      // Fallback to hardcoded roles if API fails
      setAvailableRoles([
        { id: 'admin', label: 'Administrator', description: 'Full access to all features and settings' },
        { id: 'manager', label: 'Manager', description: 'Can manage inventory, warehouses, and view reports' },
        { id: 'technician', label: 'Technician', description: 'Can check out/return equipment and mark as installed' },
        { id: 'user', label: 'Regular User', description: 'Can perform basic inventory operations' },
        { id: 'viewer', label: 'Viewer', description: 'Read-only access to view inventory and reports' }
      ]);
    }
  };

  /**
   * Fetch users from the API
   */
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      // In a real implementation, you would call the API to get users
      // For now, we'll use placeholder implementation
      const response = await api.get('/auth/users');
      setUsers(response.data.users);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');

      // Fallback mock data for development
      setUsers([
        { id: 'user1', email: 'admin@example.com', roles: ['admin', 'user'] },
        { id: 'user2', email: 'manager@example.com', roles: ['manager', 'user'] },
        { id: 'user3', email: 'user@example.com', roles: ['user'] }
      ]);
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Handle role checkbox changes
   */
  const handleRoleChange = (role) => {
    setSelectedRoles({
      ...selectedRoles,
      [role]: !selectedRoles[role]
    });
  };
  
  /**
   * Reset the form
   */
  const resetForm = () => {
    setUserEmail('');
    setUserName('');
    // Reset all roles to false except 'user' which defaults to true
    const resetRoles = {};
    availableRoles.forEach(role => {
      resetRoles[role.id] = role.id === 'user';
    });
    setSelectedRoles(resetRoles);
    setEditingUserId(null);
  };
  
  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    // Convert selected roles to array
    const roles = Object.keys(selectedRoles).filter(role => selectedRoles[role]);
    
    try {
      if (editingUserId) {
        // Update existing user
        const updateData = {
          name: userName,
          roles,
          isActive: selectedRoles.active !== false,
          needsApproval: false // Approve user when admin updates
        };

        await api.put(`/auth/users/${editingUserId}/roles`, updateData);

        // Update local state
        setUsers(users.map(user =>
          user.id === editingUserId ? { ...user, ...updateData } : user
        ));

        // Show success message
        setSnackbar({
          open: true,
          message: 'User roles updated successfully',
          severity: 'success'
        });
      } else {
        // Add new user roles
        const response = await api.post('/auth/users/roles', { email: userEmail, name: userName, roles });

        // Refresh user list from server to ensure sync
        await fetchUsers();

        // Show success message
        setSnackbar({
          open: true,
          message: 'User created successfully',
          severity: 'success'
        });
      }

      // Reset form after successful submission
      resetForm();
    } catch (err) {
      console.error('Error updating roles:', err);
      const errorMessage = err.message || 'Failed to update roles. Please try again.';
      setError(errorMessage);

      // Show error toast
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Handle edit user button click
   */
  const handleEditUser = (user) => {
    setEditingUserId(user.id);
    setUserEmail(user.email);
    setUserName(user.name || '');

    // Build roles dynamically from availableRoles instead of hardcoding
    const roles = {};
    availableRoles.forEach(role => {
      roles[role.id] = false;
    });

    // Set user's current roles to true
    user.roles.forEach(role => {
      roles[role] = true;
    });

    setSelectedRoles(roles);
  };
  
  /**
   * Open confirmation dialog for deleting user roles
   */
  const openDeleteConfirm = (user) => {
    setConfirmDialog({
      open: true,
      userId: user.id,
      email: user.email
    });
  };
  
  /**
   * Close confirmation dialog
   */
  const closeDeleteConfirm = () => {
    setConfirmDialog({
      open: false,
      userId: null,
      email: ''
    });
  };
  
  /**
   * Handle delete user roles
   */
  const handleDeleteUserRoles = async () => {
    setLoading(true);

    try {
      // Delete user from API
      await api.delete(`/auth/users/${confirmDialog.userId}`);

      // Refresh user list from server
      await fetchUsers();

      // Show success message
      setSnackbar({
        open: true,
        message: 'User removed successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error deleting user roles:', err);
      const errorMessage = err.message || 'Failed to delete user roles. Please try again.';
      setError(errorMessage);

      // Show error toast
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
      closeDeleteConfirm();
    }
  };

  /**
   * Close snackbar notification
   */
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Typography variant="h4" gutterBottom>
        User Role Management
      </Typography>
      
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {editingUserId ? 'Edit User Roles' : 'Add User Roles'}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <TextField
              label="User Email"
              type="email"
              fullWidth
              required
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              disabled={editingUserId !== null}
              sx={{ mb: 3 }}
            />

            <TextField
              label="User Name"
              type="text"
              fullWidth
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              sx={{ mb: 3 }}
            />
            
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend">Assign Roles</FormLabel>
              <FormGroup>
                {availableRoles.map(role => (
                  <FormControlLabel
                    key={role.id}
                    control={
                      <Checkbox 
                        checked={selectedRoles[role.id]} 
                        onChange={() => handleRoleChange(role.id)} 
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1">{role.label}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {role.description}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
            </FormControl>
            
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  editingUserId ? 'Update Roles' : 'Assign Roles'
                )}
              </Button>
              
              {editingUserId && (
                <Button 
                  type="button" 
                  variant="outlined"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </form>
        </CardContent>
      </Card>
      
      <Typography variant="h6" gutterBottom>
        Current Users
      </Typography>
      
      {loading && !users.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : users.length > 0 ? (
        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Registration</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map(user => (
                <TableRow key={user.id}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.name || '-'}</TableCell>
                  <TableCell>
                    {user.roles && user.roles.map(role => {
                      const roleInfo = availableRoles.find(r => r.id === role);
                      return (
                        <Box 
                          key={role} 
                          component="span" 
                          sx={{ 
                            display: 'inline-block',
                            bgcolor: role === 'admin' ? 'error.light' : 
                                    role === 'manager' ? 'warning.light' : 
                                    role === 'user' ? 'info.light' : 
                                    role === 'viewer' ? 'success.light' : 'text.disabled',
                            color: 'background.paper',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            mr: 1,
                            mb: 0.5,
                            fontSize: '0.75rem'
                          }}
                        >
                          {roleInfo ? roleInfo.label : role}
                        </Box>
                      );
                    })}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {user.isActive ? (
                        <Box component="span" sx={{ color: 'success.main' }}>Active</Box>
                      ) : (
                        <Box component="span" sx={{ color: 'error.main' }}>Inactive</Box>
                      )}
                      {user.needsApproval && (
                        <Box 
                          component="span" 
                          sx={{ 
                            bgcolor: 'warning.main', 
                            color: 'white', 
                            px: 1, 
                            py: 0.25, 
                            borderRadius: 1,
                            fontSize: '0.75rem'
                          }}
                        >
                          Pending
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box component="span" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                      {user.registrationMethod || 'pre-register'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleEditUser(user)}
                      color="primary"
                      aria-label="edit user roles"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => openDeleteConfirm(user)}
                      color="error"
                      aria-label="delete user roles"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info">No users found. Add a user to get started.</Alert>
      )}
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeDeleteConfirm}
      >
        <DialogTitle>Confirm Removal</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove all roles for user <strong>{confirmDialog.email}</strong>?
            This will revoke their access to the system.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteUserRoles} color="error" variant="contained">
            {loading ? <CircularProgress size={24} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserRoleManager;